/**
 * Global number formatting utilities
 * Provides consistent comma separator formatting across the application
 */

/**
 * Formats a number with comma separators for display
 * @param value - The number to format
 * @returns Formatted string with comma separators
 */
export const formatNumberWithCommas = (value: number | string | null | undefined): string => {
  if (value === null || value === undefined || value === '') {
    return '0';
  }
  
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) {
    return '0';
  }
  
  return new Intl.NumberFormat('en-US').format(numValue);
};

/**
 * Formats a number with dot separators for display
 * @param value - The number to format
 * @returns Formatted string with dot separators
 */
export const formatNumberWithDot = (value: number | string | null | undefined): string => {
    if (value === null || value === undefined) return '';
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
};

/**
 * Formats a number with dot as thousands separator and comma as decimal separator
 * Example: 17268.5185 => "17.268,5185"
 */
export const formatNumberWithDotThousandCommaDecimal = (value: number | string | null | undefined): string => {
  if (value === null || value === undefined || value === '') {
    return '0';
  }
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) {
    return '0';
  }
  return new Intl.NumberFormat('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 20 }).format(numValue);
};

/**
 * Formats currency in Vietnamese format with ₫ symbol
 * @param amount - The amount to format
 * @returns Formatted currency string
 */
export const formatVietnamCurrency = (amount: number | string | null | undefined): string => {
  if (amount === null || amount === undefined || amount === '') {
    return '0 ₫';
  }
  
  const numValue = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (isNaN(numValue)) {
    return '0 ₫';
  }
  
  return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(numValue);
};

/**
 * Parses a formatted number string back to a number
 * Removes commas and other formatting characters
 * @param value - The formatted string to parse
 * @returns Parsed number or 0 if invalid
 */
export const parseFormattedNumber = (value: string | null | undefined): number => {
  if (!value) return 0;
  
  // Remove commas, spaces, and currency symbols
  const cleanValue = value.replace(/[,\s₫$]/g, '');
  const parsed = parseFloat(cleanValue);
  
  return isNaN(parsed) ? 0 : parsed;
};

/**
 * Formatter function for Ant Design InputNumber components
 * @param value - The value to format
 * @returns Formatted string with commas
 */
export const inputNumberFormatter = (value: string | number | undefined): string => {
  if (!value) return '';
  return `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

/**
 * Parser function for Ant Design InputNumber components
 * @param value - The formatted value to parse
 * @returns Parsed number
 */
export const inputNumberParser = (value: string | undefined): number => {
  if (!value) return 0;
  const parsed = value.replace(/\$\s?|(,*)/g, '');
  return parseFloat(parsed) || 0;
};